import { z } from 'zod';

// ── Module CRUD ───────────────────────────────────────────────────────────────

export const CreateModuleSchema = z.object({
  key: z
    .string()
    .min(2)
    .max(50)
    .regex(/^[a-z][a-z0-9_]*$/, 'Key must be lowercase alphanumeric with underscores'),
  name: z.string().min(2).max(100),
  description: z.string().max(500).optional(),
  icon: z.string().max(50).optional(),
  displayOrder: z.number().int().min(0).optional(),
});

export type CreateModuleRequest = z.infer<typeof CreateModuleSchema>;

export const UpdateModuleSchema = CreateModuleSchema.partial().extend({
  isActive: z.boolean().optional(),
});

export type UpdateModuleRequest = z.infer<typeof UpdateModuleSchema>;

// ── Capability CRUD ───────────────────────────────────────────────────────────

export const CreateCapabilitySchema = z.object({
  key: z
    .string()
    .min(2)
    .max(100)
    .regex(
      /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/,
      'Key must follow module.capability format (e.g. analytics.trends)'
    ),
  name: z.string().min(2).max(100),
  description: z.string().max(500).optional(),
  suggestedTier: z.enum(['Free', 'Basic', 'Premium']).optional(),
  displayOrder: z.number().int().min(0).optional(),
});

export type CreateCapabilityRequest = z.infer<typeof CreateCapabilitySchema>;

export const UpdateCapabilitySchema = CreateCapabilitySchema.partial().extend({
  isActive: z.boolean().optional(),
});

export type UpdateCapabilityRequest = z.infer<typeof UpdateCapabilitySchema>;

// ── Plan Capability Assignment ────────────────────────────────────────────────

export const PlanCapabilityItemSchema = z.object({
  capabilityId: z.string().uuid(),
  limitValue: z.number().int().min(0).nullable().optional(),
});

export const AssignPlanCapabilitiesSchema = z.object({
  capabilities: z.array(PlanCapabilityItemSchema),
});

export type AssignPlanCapabilitiesRequest = z.infer<typeof AssignPlanCapabilitiesSchema>;

// ── Vendor Override ───────────────────────────────────────────────────────────

export const CreateVendorOverrideSchema = z.object({
  capabilityId: z.string().uuid(),
  reason: z.string().min(1).max(500),
});

export type CreateVendorOverrideRequest = z.infer<typeof CreateVendorOverrideSchema>;

// ── Gate Event Batch ──────────────────────────────────────────────────────────

export const GateEventItemSchema = z.object({
  capabilityKey: z.string().min(1).max(100),
  action: z.enum(['CLICKED', 'VIEWED', 'DISMISSED']),
  metadata: z.record(z.unknown()).optional(),
});

export const GateEventBatchSchema = z.object({
  events: z.array(GateEventItemSchema).min(1).max(10),
});

export type GateEventBatchRequest = z.infer<typeof GateEventBatchSchema>;

// ── Response Schemas ──────────────────────────────────────────────────────────

export const CapabilityResponseSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  suggestedTier: z.string(),
  displayOrder: z.number(),
  isActive: z.boolean(),
  moduleId: z.string().uuid(),
});

export const ModuleResponseSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  displayOrder: z.number(),
  isActive: z.boolean(),
  capabilities: z.array(CapabilityResponseSchema),
});

export const GateEventStatsSchema = z.object({
  capabilityKey: z.string(),
  totalHits: z.number(),
  clickedCount: z.number(),
  viewedCount: z.number(),
  dismissedCount: z.number(),
  uniqueVendors: z.number(),
});
